import type { MouseEvent, ReactNode } from "react";

import { ExternalLink } from "lucide-react";

import { Link } from "react-router-dom";



import {

  BatchDetailLink,

  WorkOrderDetailLink,

} from "@/components/hub/HubEntityDetailLink";

import type { BatchPeekContext } from "@/components/hub/entityPeekTypes";

import { MrpSkuHoverPeek } from "@/components/mrp/MrpSkuHoverPeek";

import { buildManufacturingTransfersWorkbenchPath } from "@/lib/hubEntityDetailPaths";

import type { MrpSkuHoverHintFields } from "@/lib/mrp";

import { cn } from "@/lib/utils";

import { megaventoryUrlFor } from "@/pages/manufacturing/workOrdersPageModel";



import type { KittingCartSlot } from "./kittingCartModel";

import { kittingBatchPeekContext } from "./kittingBatchPeek";



const HUB_LINK_CLASS =

  "font-mono font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1";



const MEGAVENTORY_LINK_CLASS =

  "inline-flex shrink-0 rounded p-0.5 text-sky-700 hover:text-sky-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1";



function MegaventoryWorkOrderExternalLink({

  mvId,

  mvNumber,

  workOrderNumber,

}: {

  mvId: number;

  mvNumber?: string | null;

  workOrderNumber?: string | null;

}): ReactNode {

  const href = megaventoryUrlFor({ mv_id: mvId, mv_number: mvNumber ?? workOrderNumber ?? null });

  if (href == null) return null;

  return (

    <a

      href={href}

      target="_blank"

      rel="noreferrer noopener"

      className={MEGAVENTORY_LINK_CLASS}

      aria-label={`Open ${workOrderNumber ?? "work order"} in Megaventory`}

      onClick={(event) => event.stopPropagation()}

    >

      <ExternalLink className="h-3.5 w-3.5" aria-hidden />

    </a>

  );

}



export function KittingPickSkuCell({

  sku,

  hint,

  hintsLoading,

  onHoverIntent,

}: {

  sku: string;

  hint?: MrpSkuHoverHintFields;

  hintsLoading?: boolean;

  onHoverIntent?: () => void;

}): ReactNode {

  return (

    <MrpSkuHoverPeek

      sku={sku}

      hint={hint}

      hintsLoading={hintsLoading}

      onHoverIntent={onHoverIntent}

      tab="inventory"

      showUnitLabel

    />

  );

}



function isolateCardNavigation(event: MouseEvent): void {
  event.stopPropagation();
}

function CardSafeEntityTrigger({
  stopCardNavigation,
  children,
}: {
  stopCardNavigation?: boolean;
  children: ReactNode;
}): ReactNode {
  if (!stopCardNavigation) return children;
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5" onClick={isolateCardNavigation}>
      {children}
    </span>
  );
}

export function KittingCartSlotHeadline({
  slot,
  batchPeek,
  batchPeekLoading,
  variant = "row",
  stopCardNavigation = false,
}: {
  slot: KittingCartSlot;
  batchPeek?: BatchPeekContext;
  batchPeekLoading?: boolean;
  variant?: "hero" | "row";
  stopCardNavigation?: boolean;
}): ReactNode {
  const headlineClass =
    variant === "hero"
      ? "text-lg font-semibold leading-tight"
      : "text-sm font-bold leading-tight";

  if (slot.kind === "batch" && slot.batchIds.length > 0) {
    const batchId = slot.batchIds[0]!;
    const peek =
      batchPeek ??
      kittingBatchPeekContext({
        linkedWorkOrderNumbers: slot.linkedWorkOrderNumbers,
        loading: batchPeekLoading,
      });
    return (
      <CardSafeEntityTrigger stopCardNavigation={stopCardNavigation}>
        <BatchDetailLink batchId={batchId} className={cn("font-mono", headlineClass)} peek={peek}>
          Batch {batchId}
        </BatchDetailLink>
        <span className={cn("font-medium text-slate-700", variant === "hero" ? "text-lg" : "text-sm")}>
          {" "}
          · {slot.deliveryStationName}
        </span>
      </CardSafeEntityTrigger>
    );
  }

  if (slot.kind === "work_order" && slot.workOrderId != null) {
    return (
      <CardSafeEntityTrigger stopCardNavigation={stopCardNavigation}>
        <WorkOrderDetailLink
          buildOrderId={slot.workOrderId}
          workOrderNumber={slot.workOrderNumber}
          className={headlineClass}
        />
        <span className={cn("font-medium text-slate-700", variant === "hero" ? "text-lg" : "text-sm")}>
          {" "}
          · {slot.deliveryStationName}
        </span>
        {slot.workOrderMvId != null ? (
          <MegaventoryWorkOrderExternalLink
            mvId={slot.workOrderMvId}
            mvNumber={slot.workOrderMvNumber}
            workOrderNumber={slot.workOrderNumber}
          />
        ) : null}
      </CardSafeEntityTrigger>
    );
  }

  if (slot.kind === "transfer_order" && slot.transferOrderId != null) {
    return (
      <CardSafeEntityTrigger stopCardNavigation={stopCardNavigation}>
        <Link
          to={buildManufacturingTransfersWorkbenchPath({
            transferOrderId: slot.transferOrderId,
            search: slot.transferNumber ?? slot.headlineLabel,
          })}
          className={cn(
            "font-mono text-sky-800 underline decoration-sky-800/40 hover:text-sky-950",
            headlineClass,
          )}
        >
          {slot.headlineLabel}
        </Link>
      </CardSafeEntityTrigger>
    );
  }

  return <span className={cn("font-mono text-slate-900", headlineClass)}>{slot.headlineLabel}</span>;
}



export function KittingDestinationBinLink({

  slot,

  label,

  batchPeek,

  batchPeekLoading,

  className,

}: {

  slot: KittingCartSlot;

  label: string;

  batchPeek?: BatchPeekContext;

  batchPeekLoading?: boolean;

  className?: string;

}): ReactNode {

  if (slot.kind === "batch" && slot.batchIds.length > 0) {

    const batchId = slot.batchIds[0]!;

    const peek =

      batchPeek ??

      kittingBatchPeekContext({

        linkedWorkOrderNumbers: slot.linkedWorkOrderNumbers,

        loading: batchPeekLoading,

      });

    return (

      <BatchDetailLink batchId={batchId} className={cn(HUB_LINK_CLASS, "text-xs font-semibold", className)} peek={peek}>

        {label}

      </BatchDetailLink>

    );

  }

  if (slot.kind === "work_order" && slot.workOrderId != null) {

    return (

      <WorkOrderDetailLink

        buildOrderId={slot.workOrderId}

        workOrderNumber={slot.workOrderNumber ?? label}

        className={cn(HUB_LINK_CLASS, "text-xs font-semibold", className)}

      >

        {label}

      </WorkOrderDetailLink>

    );

  }

  if (slot.kind === "transfer_order" && slot.transferOrderId != null) {

    return (

      <Link

        to={buildManufacturingTransfersWorkbenchPath({

          transferOrderId: slot.transferOrderId,

          search: slot.transferNumber ?? label,

        })}

        className={cn(HUB_LINK_CLASS, "text-xs font-semibold", className)}

      >

        {label}

      </Link>

    );

  }

  return <span className={cn("text-xs font-semibold tabular-nums text-slate-900", className)}>{label}</span>;

}


